import React, { useEffect, useState } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Users,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  FileText,
  Euro,
  Settings2,
  X,
  Trash2,
  List,
  ShoppingCart,
  CreditCard,
  Wallet,
  Loader2,
  LayoutGrid,
  TableIcon,
  MoreHorizontal
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RelationshipsPage = () => {
  const navigate = useNavigate();
  const { isBuyer, isSeller } = useAuth();
  const { t } = useI18n();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [relationships, setRelationships] = useState([]);
  const [pricelists, setPricelists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [selectedPricelist, setSelectedPricelist] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // View mode: 'cards' or 'table'
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    const saved = localStorage.getItem('relationships_view_mode');
    return (saved === 'table' || saved === 'cards') ? saved : 'cards';
  });
  
  // Persist view mode
  const handleViewModeChange = (mode: 'cards' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('relationships_view_mode', mode);
  };
  
  // Credit management
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [creditBuyer, setCreditBuyer] = useState<any>(null);
  const [creditSettings, setCreditSettings] = useState({
    credit_enabled: false,
    credit_type: 'simple' as 'simple' | 'revolving',
    credit_limit: 0,
    billing_cycle: 'monthly' as 'weekly' | 'biweekly' | 'monthly' | 'quarterly',
    billing_day: 1,
    alert_threshold_percent: 80,
    block_on_limit: true,
    notes: ''
  });
  const [creditLoading, setCreditLoading] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [creditThresholds, setCreditThresholds] = useState<{value: number, label: string}[]>([]);

  useEffect(() => {
    fetchData();
    if (isSeller) {
      checkBillingExtension();
      fetchCreditThresholds();
    }
  }, []);

  const checkBillingExtension = async () => {
    try {
      const res = await axios.get(`${API}/billing/settings`);
      setBillingEnabled(res.data.extension_active && res.data.settings?.enabled);
    } catch (error) {
      console.error('Error checking billing:', error);
    }
  };

  const fetchCreditThresholds = async () => {
    try {
      const res = await axios.get(`${API}/billing/credit-thresholds`);
      setCreditThresholds(res.data.thresholds);
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/relationships`);
      setRelationships(res.data);
      
      if (isSeller) {
        const pricelistRes = await axios.get(`${API}/pricelists`);
        setPricelists(pricelistRes.data);
      }
    } catch (error) {
      console.error('Error fetching relationships:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: Clock },
      accepted: { color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', icon: CheckCircle },
      active: { color: 'bg-green-500/10 text-green-600 border-green-500/20', icon: CheckCircle },
      rejected: { color: 'bg-red-500/10 text-red-600 border-red-500/20', icon: XCircle },
      paused: { color: 'bg-gray-500/10 text-gray-600 border-gray-500/20', icon: Pause },
      terminated: { color: 'bg-gray-500/10 text-gray-600 border-gray-500/20', icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
        <Icon className="h-3 w-3" />
        {t(`relationships.${status}`)}
      </span>
    );
  };

  const handleAcceptClick = (rel) => {
    setSelectedRelationship(rel);
    setSelectedPricelist('');
    setShowAcceptDialog(true);
  };

  // Credit management functions
  const handleOpenCreditDialog = async (rel) => {
    setCreditBuyer(rel);
    setCreditLoading(true);
    setShowCreditDialog(true);
    
    try {
      const res = await axios.get(`${API}/billing/buyer-credits/${rel.buyer_id}`);
      setCreditSettings({
        credit_enabled: res.data.credit_enabled || false,
        credit_type: res.data.credit_type || 'simple',
        credit_limit: res.data.credit_limit || 0,
        billing_cycle: res.data.billing_cycle || 'monthly',
        billing_day: res.data.billing_day || 1,
        alert_threshold_percent: res.data.alert_threshold_percent || 80,
        block_on_limit: res.data.block_on_limit !== false,
        notes: res.data.notes || ''
      });
    } catch (error) {
      // Default values
      setCreditSettings({
        credit_enabled: false,
        credit_type: 'simple',
        credit_limit: 0,
        billing_cycle: 'monthly',
        billing_day: 1,
        alert_threshold_percent: 80,
        block_on_limit: true,
        notes: ''
      });
    } finally {
      setCreditLoading(false);
    }
  };

  const handleSaveCredit = async () => {
    if (!creditBuyer) return;
    
    setCreditLoading(true);
    try {
      await axios.put(`${API}/billing/buyer-credits/${creditBuyer.buyer_id}`, creditSettings);
      toast.success('Impostazioni credito salvate');
      setShowCreditDialog(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setCreditLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedPricelist) {
      toast.error('Seleziona un listino');
      return;
    }

    setActionLoading(true);
    try {
      await axios.put(
        `${API}/relationships/${selectedRelationship.id}/accept?pricelist_id=${selectedPricelist}`
      );
      toast.success('Relazione attivata');
      setShowAcceptDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (relId) => {
    const ok = await confirm({ title: 'Rifiuta richiesta', description: 'Sei sicuro di voler rifiutare questa richiesta?', confirmText: 'Rifiuta', variant: 'destructive' });
    if (!ok) return;

    try {
      await axios.put(`${API}/relationships/${relId}/reject`);
      toast.success('Richiesta rifiutata');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  // Annulla richiesta pending (per buyer)
  const handleCancelRequest = async (relId) => {
    const ok = await confirm({ title: 'Annulla richiesta', description: 'Sei sicuro di voler annullare questa richiesta?', confirmText: 'Annulla richiesta', variant: 'destructive' });
    if (!ok) return;

    try {
      await axios.delete(`${API}/relationships/${relId}`);
      toast.success('Richiesta annullata');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  // Termina relazione attiva
  const handleTerminateRelationship = async (relId) => {
    const ok = await confirm({ title: 'Termina relazione', description: 'Sei sicuro di voler terminare questa relazione commerciale? Il cliente non potrà più effettuare ordini.', confirmText: 'Termina', variant: 'destructive' });
    if (!ok) return;

    try {
      await axios.delete(`${API}/relationships/${relId}`);
      toast.success('Relazione terminata');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  const pendingRelationships = relationships.filter(r => r.status === 'pending');
  const activeRelationships = relationships.filter(r => r.status === 'active');
  const otherRelationships = relationships.filter(r => !['pending', 'active'].includes(r.status));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relationships-page">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t('relationships.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {isBuyer ? 'I tuoi fornitori' : 'I tuoi clienti'}
          </p>
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          <Button
            variant={viewMode === 'cards' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('cards')}
            className="gap-2"
            data-testid="view-cards-btn"
          >
            <LayoutGrid className="h-4 w-4" />
            Card
          </Button>
          <Button
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleViewModeChange('table')}
            className="gap-2"
            data-testid="view-table-btn"
          >
            <TableIcon className="h-4 w-4" />
            Tabella
          </Button>
        </div>
      </div>

      {/* Pending Requests */}
      {pendingRelationships.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            {t('relationships.pending')} ({pendingRelationships.length})
          </h2>
          {viewMode === 'cards' ? (
            // CARD VIEW
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingRelationships.map((rel) => (
                <Card key={rel.id} className="border-amber-500/30" data-testid={`pending-rel-${rel.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{rel.partner_company?.name || 'Azienda'}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {rel.partner_company?.type}
                        </p>
                        {getStatusBadge(rel.status)}
                      </div>
                    </div>
                    
                    {isSeller && (
                      <div className="flex gap-2 mt-4">
                        <Button 
                          className="flex-1"
                          onClick={() => handleAcceptClick(rel)}
                          data-testid={`accept-rel-${rel.id}`}
                        >
                          {t('relationships.accept')}
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleReject(rel.id)}
                          data-testid={`reject-rel-${rel.id}`}
                        >
                          {t('relationships.reject')}
                        </Button>
                      </div>
                    )}
                    
                    {isBuyer && (
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-muted-foreground">
                          {t('relationships.waitingApproval')}
                        </p>
                        <Button 
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleCancelRequest(rel.id)}
                          data-testid={`cancel-rel-${rel.id}`}
                          title="Annulla richiesta"
                        >
                          <X className="h-5 w-5" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // TABLE VIEW
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Azienda</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Data Richiesta</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRelationships.map((rel) => (
                    <TableRow key={rel.id} data-testid={`pending-rel-table-${rel.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium">{rel.partner_company?.name || 'Azienda'}</p>
                            <p className="text-xs text-muted-foreground">{rel.partner_company?.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{rel.partner_company?.type}</TableCell>
                      <TableCell>{getStatusBadge(rel.status)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(rel.created_at).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell className="text-right">
                        {isSeller ? (
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" onClick={() => handleAcceptClick(rel)}>
                              Accetta
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleReject(rel.id)}>
                              Rifiuta
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleCancelRequest(rel.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Annulla
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      )}

      {/* Active Relationships */}
      <div className="space-y-4">
        <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          {t('relationships.active')} ({activeRelationships.length})
        </h2>
        {activeRelationships.length > 0 ? (
          viewMode === 'cards' ? (
            // CARD VIEW
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeRelationships.map((rel) => (
                <Card key={rel.id} className="border-green-500/30" data-testid={`active-rel-${rel.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{rel.partner_company?.name || 'Azienda'}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {rel.partner_company?.type}
                        </p>
                        {getStatusBadge(rel.status)}
                      </div>
                    </div>
                  
                    <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <span>
                              Listino assegnato
                            </span>
                          </div>
                          <p className="text-xs mt-1">
                            Dal {new Date(rel.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {/* Pulsante Termina Relazione */}
                        <Button 
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleTerminateRelationship(rel.id)}
                          data-testid={`terminate-rel-${rel.id}`}
                          title="Termina relazione"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  
                    {/* Pulsanti Seller: Gestisci Listino + Storico Ordini */}
                    {isSeller && (
                      <div className="mt-4 space-y-2">
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => navigate(`/relationships/${rel.id}/pricelist`)}
                          data-testid={`manage-pricelist-${rel.id}`}
                        >
                          <Euro className="h-4 w-4 mr-2" />
                          Gestisci Listino Cliente
                        </Button>
                        
                        {/* Storico Ordini - sempre visibile per clienti attivi */}
                        <Button
                          variant={rel.orders_count > 0 ? 'secondary' : 'ghost'}
                          className="w-full"
                          onClick={() => navigate(`/orders?buyer_company_id=${rel.buyer_company_id}`)}
                          data-testid={`view-orders-${rel.id}`}
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Storico Ordini
                          {rel.orders_count > 0 && (
                            <span className="ml-auto bg-primary/15 text-primary text-xs font-medium px-1.5 py-0.5 rounded-full">
                              {rel.orders_count}
                            </span>
                          )}
                        </Button>
                        
                        {/* Pulsante Gestisci Credito - Solo se billing attivo */}
                        {billingEnabled && (
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={() => handleOpenCreditDialog(rel)}
                            data-testid={`manage-credit-${rel.id}`}
                          >
                            <Wallet className="h-4 w-4 mr-2" />
                            Gestisci Credito
                          </Button>
                        )}
                      </div>
                    )}
                  
                    {/* Pulsanti Scorri Listino e Ordina - Solo Buyer */}
                    {isBuyer && (
                      <div className="mt-4 flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => navigate(`/catalog/${rel.seller_company_id}`)}
                          data-testid={`browse-catalog-${rel.id}`}
                        >
                          <List className="h-4 w-4 mr-2" />
                          Listino
                        </Button>
                        <Button 
                          className="flex-1"
                          onClick={() => navigate(`/orders/new?seller=${rel.seller_company_id}`)}
                          data-testid={`order-from-${rel.id}`}
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          Ordina
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // TABLE VIEW
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Azienda</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Dal</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeRelationships.map((rel) => (
                    <TableRow key={rel.id} data-testid={`active-rel-table-${rel.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">{rel.partner_company?.name || 'Azienda'}</p>
                            <p className="text-xs text-muted-foreground">{rel.partner_company?.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{rel.partner_company?.type}</TableCell>
                      <TableCell>{getStatusBadge(rel.status)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(rel.created_at).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isSeller && (
                              <>
                                <DropdownMenuItem onClick={() => navigate(`/relationships/${rel.id}/pricelist`)}>
                                  <Euro className="h-4 w-4 mr-2" />
                                  Gestisci Listino
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/orders?buyer_company_id=${rel.buyer_company_id}`)}>
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                  Storico Ordini
                                  {rel.orders_count > 0 && (
                                    <span className="ml-auto text-xs text-muted-foreground">{rel.orders_count}</span>
                                  )}
                                </DropdownMenuItem>
                                {billingEnabled && (
                                  <DropdownMenuItem onClick={() => handleOpenCreditDialog(rel)}>
                                    <Wallet className="h-4 w-4 mr-2" />
                                    Gestisci Credito
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            {isBuyer && (
                              <>
                                <DropdownMenuItem onClick={() => navigate(`/catalog/${rel.seller_company_id}`)}>
                                  <List className="h-4 w-4 mr-2" />
                                  Vedi Listino
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/orders/new?seller=${rel.seller_company_id}`)}>
                                  <ShoppingCart className="h-4 w-4 mr-2" />
                                  Nuovo Ordine
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleTerminateRelationship(rel.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Termina Relazione
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('relationships.noRelationships')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Other Relationships */}
      {otherRelationships.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-heading font-semibold">Altre ({otherRelationships.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherRelationships.map((rel) => (
              <Card key={rel.id} className="opacity-60" data-testid={`other-rel-${rel.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{rel.partner_company?.name || 'Azienda'}</h3>
                      <p className="text-sm text-muted-foreground capitalize">
                        {rel.partner_company?.type}
                      </p>
                      {getStatusBadge(rel.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{t('relationships.accept')}</DialogTitle>
            <DialogDescription>
              Seleziona il listino da assegnare a {selectedRelationship?.partner_company?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPricelist} onValueChange={setSelectedPricelist}>
              <SelectTrigger data-testid="select-pricelist-accept">
                <SelectValue placeholder={t('relationships.assignPricelist')} />
              </SelectTrigger>
              <SelectContent>
                {pricelists.map(pl => (
                  <SelectItem key={pl.id} value={pl.id}>
                    {pl.name} {pl.is_standard && '(Standard)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleAccept}
              disabled={actionLoading || !selectedPricelist}
              data-testid="confirm-accept-btn"
            >
              {actionLoading ? t('common.loading') : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Management Dialog */}
      <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" scrollable>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Gestione Credito
            </DialogTitle>
            <DialogDescription>
              Configura il credito per {creditBuyer?.partner_company?.name}
            </DialogDescription>
          </DialogHeader>
          
          {creditLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Abilitazione credito */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Cliente Fidato</Label>
                  <p className="text-sm text-muted-foreground">
                    Permetti accumulo ordini prima del pagamento
                  </p>
                </div>
                <Switch
                  checked={creditSettings.credit_enabled}
                  onCheckedChange={(checked) => 
                    setCreditSettings({ ...creditSettings, credit_enabled: checked })
                  }
                  data-testid="credit-enabled-switch"
                />
              </div>

              {creditSettings.credit_enabled && (
                <>
                  {/* Tipo credito */}
                  <div className="space-y-3">
                    <Label>Tipo Credito</Label>
                    <Select
                      value={creditSettings.credit_type}
                      onValueChange={(value: 'simple' | 'revolving') => 
                        setCreditSettings({ ...creditSettings, credit_type: value })
                      }
                    >
                      <SelectTrigger data-testid="credit-type-select">
                        <SelectValue placeholder="Seleziona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simple">
                          <div className="flex flex-col">
                            <span>Semplice</span>
                            <span className="text-xs text-muted-foreground">Credito una tantum, si azzera con il pagamento</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="revolving">
                          <div className="flex flex-col">
                            <span>Fido Rotativo</span>
                            <span className="text-xs text-muted-foreground">Si ripristina automaticamente ad ogni ciclo</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {creditSettings.credit_type === 'simple' 
                        ? 'Il credito si esaurisce con gli ordini e si azzera quando il cliente paga.' 
                        : 'Il credito si ripristina automaticamente secondo il ciclo di fatturazione configurato.'}
                    </p>
                  </div>

                  {/* Soglia credito */}
                  <div className="space-y-3">
                    <Label>Soglia Credito</Label>
                    <Select
                      value={String(creditSettings.credit_limit)}
                      onValueChange={(value) => 
                        setCreditSettings({ ...creditSettings, credit_limit: Number(value) })
                      }
                    >
                      <SelectTrigger data-testid="credit-limit-select">
                        <SelectValue placeholder="Seleziona soglia" />
                      </SelectTrigger>
                      <SelectContent>
                        {creditThresholds.map(t => (
                          <SelectItem key={t.value} value={String(t.value)}>
                            {t.label}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Personalizzato...</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Input personalizzato */}
                    {!creditThresholds.some(t => t.value === creditSettings.credit_limit) && creditSettings.credit_limit > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">€</span>
                        <Input
                          type="number"
                          value={creditSettings.credit_limit}
                          onChange={(e) => 
                            setCreditSettings({ ...creditSettings, credit_limit: Number(e.target.value) })
                          }
                          min={0}
                          step={50}
                          className="w-32"
                          data-testid="credit-limit-custom"
                        />
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      Il cliente può accumulare ordini fino a {formatCurrency(creditSettings.credit_limit)} prima di dover pagare.
                    </p>
                  </div>

                  {/* Configurazione Fido Rotativo */}
                  {creditSettings.credit_type === 'revolving' && (
                    <div className="space-y-4 p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <div className="flex items-center gap-2 text-blue-600">
                        <CreditCard className="h-4 w-4" />
                        <span className="text-sm font-medium">Configurazione Fido Rotativo</span>
                      </div>
                      
                      {/* Ciclo di fatturazione */}
                      <div className="space-y-2">
                        <Label>Ciclo di Fatturazione</Label>
                        <Select
                          value={creditSettings.billing_cycle}
                          onValueChange={(value: 'weekly' | 'biweekly' | 'monthly' | 'quarterly') => 
                            setCreditSettings({ ...creditSettings, billing_cycle: value })
                          }
                        >
                          <SelectTrigger data-testid="billing-cycle-select">
                            <SelectValue placeholder="Seleziona ciclo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Settimanale</SelectItem>
                            <SelectItem value="biweekly">Bisettimanale</SelectItem>
                            <SelectItem value="monthly">Mensile</SelectItem>
                            <SelectItem value="quarterly">Trimestrale</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Giorno del ciclo */}
                      <div className="space-y-2">
                        <Label>
                          {creditSettings.billing_cycle === 'weekly' || creditSettings.billing_cycle === 'biweekly' 
                            ? 'Giorno della Settimana (1=Lunedì, 7=Domenica)' 
                            : 'Giorno del Mese (1-28)'}
                        </Label>
                        <Select
                          value={String(creditSettings.billing_day)}
                          onValueChange={(value) => 
                            setCreditSettings({ ...creditSettings, billing_day: Number(value) })
                          }
                        >
                          <SelectTrigger data-testid="billing-day-select">
                            <SelectValue placeholder="Seleziona giorno" />
                          </SelectTrigger>
                          <SelectContent>
                            {creditSettings.billing_cycle === 'weekly' || creditSettings.billing_cycle === 'biweekly' ? (
                              <>
                                <SelectItem value="1">Lunedì</SelectItem>
                                <SelectItem value="2">Martedì</SelectItem>
                                <SelectItem value="3">Mercoledì</SelectItem>
                                <SelectItem value="4">Giovedì</SelectItem>
                                <SelectItem value="5">Venerdì</SelectItem>
                                <SelectItem value="6">Sabato</SelectItem>
                                <SelectItem value="7">Domenica</SelectItem>
                              </>
                            ) : (
                              <>
                                {[1, 5, 10, 15, 20, 25, 28].map(day => (
                                  <SelectItem key={day} value={String(day)}>
                                    {day}° del mese
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Il credito si ripristina automaticamente a questo giorno del ciclo.
                        </p>
                      </div>

                      {/* Soglia alert */}
                      <div className="space-y-2">
                        <Label>Alert Utilizzo (%)</Label>
                        <Select
                          value={String(creditSettings.alert_threshold_percent)}
                          onValueChange={(value) => 
                            setCreditSettings({ ...creditSettings, alert_threshold_percent: Number(value) })
                          }
                        >
                          <SelectTrigger data-testid="alert-threshold-select">
                            <SelectValue placeholder="Seleziona soglia" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="50">50% - Alert anticipato</SelectItem>
                            <SelectItem value="70">70% - Alert moderato</SelectItem>
                            <SelectItem value="80">80% - Alert standard</SelectItem>
                            <SelectItem value="90">90% - Solo emergenze</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Riceverai una notifica quando il cliente raggiunge questa percentuale del credito.
                        </p>
                      </div>

                      {/* Blocco al limite */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="text-sm">Blocca ordini al limite</Label>
                          <p className="text-xs text-muted-foreground">
                            Impedisci nuovi ordini se il credito è esaurito
                          </p>
                        </div>
                        <Switch
                          checked={creditSettings.block_on_limit}
                          onCheckedChange={(checked) => 
                            setCreditSettings({ ...creditSettings, block_on_limit: checked })
                          }
                          data-testid="block-on-limit-switch"
                        />
                      </div>
                    </div>
                  )}

                  {/* Note */}
                  <div className="space-y-2">
                    <Label>Note (opzionale)</Label>
                    <Textarea
                      value={creditSettings.notes}
                      onChange={(e) => 
                        setCreditSettings({ ...creditSettings, notes: e.target.value })
                      }
                      placeholder="Es. Cliente storico, pagamenti sempre puntuali..."
                      rows={2}
                      data-testid="credit-notes"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSaveCredit}
              disabled={creditLoading}
              data-testid="save-credit-btn"
            >
              {creditLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                'Salva'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RelationshipsPage;
